import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { LoadingPage } from '@/components/common';
import { isDevLoginActive } from './DevLoginButton';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * AuthGuard - Route guard component
 *
 * Protects routes that require login:
 * - Shows Loading while checking
 * - Redirects to login page if not signed in
 * - Renders children if signed in (Auth0 or Dev Login)
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { t } = useTranslation('auth');
  const { isLoading, isAuthenticated } = useAuth0();
  const location = useLocation();

  // Check Dev Login state (priority)
  const isDevMode = isDevLoginActive();

  // Dev Login mode passes through directly
  if (isDevMode) {
    return <>{children}</>;
  }

  // Auth0 still loading
  if (isLoading) {
    return <LoadingPage message={t('loading.checkingAuth')} />;
  }

  // Not signed in, redirect to login page, save current path for redirect after login
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Signed in, render children
  return <>{children}</>;
}
